import { createTheme } from '@mui/material/styles';
import { tokens, light, dark, semantic, typography } from './tokens.js';

// Base config — App.jsx merges this with dynamic `mode`
export const muiThemeConfig = {
  palette: {
    primary: {
      main: tokens.colors.primary,
      dark: tokens.colors.primaryContainer,
      light: tokens.colors.inversePrimary,
      contrastText: tokens.colors.onPrimary,
    },
    secondary: {
      main: tokens.colors.secondary,
      light: tokens.colors.secondaryContainer,
      contrastText: tokens.colors.onSecondary,
    },
    error: {
      main: tokens.colors.error,
      light: tokens.colors.errorContainer,
      contrastText: tokens.colors.onError,
    },
    background: {
      default: tokens.colors.background,
      paper: tokens.colors.surfaceContainerLowest,
    },
    text: {
      primary: tokens.colors.onSurface,
      secondary: tokens.colors.onSurfaceVariant,
    },
    divider: tokens.colors.outlineVariant,
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.body,
    h1: { fontFamily: tokens.typography.fontFamily.heading, fontWeight: 700, fontSize: 48, lineHeight: '56px' },
    h2: { fontFamily: tokens.typography.fontFamily.heading, fontWeight: 600, fontSize: 32, lineHeight: '40px' },
    h3: { fontFamily: tokens.typography.fontFamily.heading, fontWeight: 600, fontSize: 24, lineHeight: '32px' },
    body1: { fontSize: 16, lineHeight: '24px' },
    body2: { fontSize: 14, lineHeight: '20px' },
    button: { textTransform: 'none', fontWeight: 600 },
    overline: { fontSize: 12, fontWeight: 600, letterSpacing: '0.05em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 600 },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },
    MuiPaper: {
      styleOverrides: {
        root: { border: `1px solid ${tokens.colors.outlineVariant}` },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            background: c.bgMuted,
            border: `1px solid ${c.borderSubtle}`,
            borderRadius: '10px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0,0,0,0.45)'
              : '0 8px 24px rgba(30,67,150,0.10)',
          };
        },
        list: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '4px 0',
            '& .MuiMenuItem-root': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 500,
              color: c.textPrimary,
              minHeight: '32px',
              padding: '6px 14px',
              '&:hover': { background: c.bgHover },
              '&.Mui-selected': { background: c.bgHover, color: c.textActive, fontWeight: 600 },
              '&.Mui-selected:hover': { background: c.bgHover },
            },
          };
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        // ── Column context menu (Popper > Paper > menuList) ───────────────────
        menu: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            '& .MuiPaper-root': {
              background: c.bgMuted,
              border: `1px solid ${c.borderSubtle}`,
              borderRadius: '10px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 24px rgba(0,0,0,0.45)'
                : '0 8px 24px rgba(30,67,150,0.10)',
              overflow: 'hidden',
            },
            '& .MuiMenuItem-root': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 500,
              color: c.textPrimary,
              padding: '6px 12px',
              minHeight: 'unset',
              '&:hover': { background: c.bgHover },
            },
            '& .MuiListItemText-primary': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 500,
              color: c.textPrimary,
            },
            '& .MuiListItemIcon-root': {
              minWidth: '26px',
              color: c.textMuted,
              '& .MuiSvgIcon-root': { fontSize: '15px', color: c.textMuted },
            },
            '& .MuiDivider-root': { borderColor: c.divider },
          };
        },

        // ── Filter panel + Columns panel (Popper > paper > panelWrapper > …) ─
        paper: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            background: c.bgMuted,
            border: `1px solid ${c.borderSubtle}`,
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.4)'
              : '0 8px 32px rgba(30,67,150,0.12)',
            overflow: 'hidden',
            fontFamily: typography.fontFamily.body,
          };
        },
        panelWrapper: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return { background: c.bgMuted };
        },
        panelHeader: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '12px 16px 8px',
            borderBottom: `1px solid ${c.divider}`,
          };
        },
        panelContent: { padding: '8px 4px' },
        panelFooter: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '8px 12px',
            borderTop: `1px solid ${c.divider}`,
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            '& .MuiButton-root': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '8px',
              padding: '5px 14px',
              textTransform: 'none',
              color: c.textActive,
            },
          };
        },

        // ── Filter form ───────────────────────────────────────────────────────
        filterForm: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '8px 12px',
            gap: '8px',
            alignItems: 'flex-end',
            fontFamily: typography.fontFamily.body,
            '& .MuiInputBase-root': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              color: c.textPrimary,
              background: c.bgSubtle,
              borderRadius: '8px',
            },
            '& .MuiInputLabel-root': {
              fontFamily: typography.fontFamily.body,
              fontSize: '11px',
              fontWeight: 600,
              color: c.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            },
            '& .MuiSelect-select': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              color: c.textPrimary,
            },
            '& .MuiSvgIcon-root': { color: c.textMuted },
          };
        },
        filterFormDeleteIcon: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            '& button': {
              color: c.textMuted,
              '&:hover': { color: semantic.error },
            },
          };
        },

        // ── Columns management panel (Hide column) ────────────────────────────
        columnsManagement: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '4px 8px',
            '& .MuiFormControlLabel-label': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 500,
              color: c.textPrimary,
            },
            '& .MuiCheckbox-root': {
              color: c.textMuted,
              padding: '4px 8px',
              '&.Mui-checked': { color: c.textActive },
            },
          };
        },
        columnsManagementHeader: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '8px 12px',
            borderBottom: `1px solid ${c.divider}`,
          };
        },
        columnsManagementFooter: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            padding: '8px 12px',
            borderTop: `1px solid ${c.divider}`,
            '& .MuiFormControlLabel-label': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              fontWeight: 500,
              color: c.textPrimary,
            },
            '& .MuiCheckbox-root': {
              color: c.textMuted,
              padding: '4px 8px',
              '&.Mui-checked': { color: c.textActive },
            },
          };
        },
        columnsManagementSearchInput: ({ theme }) => {
          const c = theme.palette.mode === 'dark' ? dark : light;
          return {
            '& input': {
              fontFamily: typography.fontFamily.body,
              fontSize: '12px',
              color: c.textPrimary,
            },
          };
        },
      },
    },
  },
};

// Keep named export for any legacy imports
export const muiTheme = createTheme({ ...muiThemeConfig, palette: { ...muiThemeConfig.palette, mode: 'light' } });
